import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, Link2, Copy, Eye, Trash2, ClipboardCheck, CheckCircle2, Clock, AlertTriangle,
  Share2, MessageCircle,
} from "lucide-react";

const STATUS_META = {
  pending: { label: "Aguardando", icon: Clock, cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  submitted: { label: "Respondido", icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  expired: { label: "Expirado", icon: AlertTriangle, cls: "bg-muted text-muted-foreground border-border" },
};

export const publicAnamnesisLink = (token) =>
  `${window.location.origin}/pre-consulta/anamnese/${token}`;

const readyMessage = (name, link) =>
  `Olá ${name || ""}! Antes da sua 1ª consulta, por favor responda a Ficha de Anamnese neste link:\n\n${link}\n\nLeva cerca de 5 minutos.`.trim();

// ─────────────────────────── Create modal ───────────────────────────
export function AnamnesisCreateModal({ onClose, onCreated, presetPatient = null }) {
  const [form, setForm] = useState({
    patient_name: presetPatient?.name || "",
    patient_email: presetPatient?.email || "",
    patient_id: presetPatient?.id || "",
    expires_in_days: 30,
  });
  const [patients, setPatients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (presetPatient) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/patients");
        setPatients(data || []);
      } catch { /* noop */ }
    })();
  }, [presetPatient]);

  const filtered = query.trim()
    ? patients
        .filter((p) => (p.name + " " + (p.email || "")).toLowerCase().includes(query.toLowerCase()))
        .slice(0, 6)
    : [];

  const pick = (p) => {
    setForm({ ...form, patient_name: p.name, patient_email: p.email || "", patient_id: p.id });
    setQuery("");
  };

  const submit = async () => {
    if (!form.patient_name.trim()) {
      toast.error("Informe o nome do paciente.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/anamnesis", {
        patient_name: form.patient_name.trim(),
        patient_email: form.patient_email.trim() || null,
        patient_id: form.patient_id || null,
        expires_in_days: Number(form.expires_in_days) || 30,
      });
      setResult(data);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar a ficha.");
    } finally {
      setSaving(false);
    }
  };

  const link = result ? publicAnamnesisLink(result.token) : "";

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Copiado!");
    } catch {
      toast.error("Não foi possível copiar. Copie manualmente.");
    }
  };

  const openWhatsapp = () => {
    const msg = readyMessage(form.patient_name, link);
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="create-anamnesis-modal"
    >
      <div
        className="bg-card border border-border rounded-sm p-6 max-w-lg w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1">Nova ficha</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">
              Ficha de Anamnese
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Gere um link público para o paciente responder antes da 1ª consulta.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" data-testid="close-create-anamnesis-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!result ? (
          <div className="space-y-3">
            {!presetPatient && (
              <div>
                <label className="overline text-muted-foreground mb-1 block">Vincular a paciente (opcional)</label>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar por nome ou e-mail…"
                  className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                  data-testid="anam-search-patient"
                />
                {filtered.length > 0 && (
                  <ul className="mt-1 border border-border rounded-sm bg-card divide-y divide-border max-h-56 overflow-y-auto">
                    {filtered.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => pick(p)}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
                          data-testid={`anam-pick-${p.id}`}
                        >
                          <div className="font-medium">{p.name}</div>
                          {p.email && <div className="text-xs text-muted-foreground">{p.email}</div>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div>
              <label className="overline text-muted-foreground mb-1 block">Nome do paciente *</label>
              <input
                value={form.patient_name}
                onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                data-testid="anam-patient-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground mb-1 block">E-mail (opcional)</label>
                <input
                  type="email"
                  value={form.patient_email}
                  onChange={(e) => setForm({ ...form, patient_email: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                  data-testid="anam-patient-email"
                />
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Expira em (dias)</label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={form.expires_in_days}
                  onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                  data-testid="anam-expires-days"
                />
              </div>
            </div>
            <div className="pt-2 flex gap-2 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors"
                data-testid="cancel-anam-btn"
              >
                Cancelar
              </button>
              <button
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
                data-testid="submit-anam-btn"
              >
                <Plus className="w-4 h-4" /> {saving ? "Gerando…" : "Gerar link"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="anam-link-result">
            <div className="rounded-sm border-2 border-emerald-500 bg-emerald-50 p-4 text-emerald-900">
              <div className="text-xs font-semibold uppercase tracking-wider">Link gerado</div>
              <div className="font-mono text-sm break-all mt-1" data-testid="anam-link-value">{link}</div>
            </div>
            <div className="rounded-sm border border-border bg-muted/30 p-4">
              <div className="overline text-muted-foreground mb-1">Mensagem pronta</div>
              <pre className="text-sm whitespace-pre-wrap font-sans" data-testid="anam-ready-message">
                {readyMessage(form.patient_name, link)}
              </pre>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                onClick={() => copy(link)}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors"
                data-testid="anam-copy-link-btn"
              >
                <Copy className="w-4 h-4" /> {copied ? "Copiado" : "Copiar link"}
              </button>
              <button
                onClick={() => copy(readyMessage(form.patient_name, link))}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors"
                data-testid="anam-copy-msg-btn"
              >
                <Share2 className="w-4 h-4" /> Copiar mensagem
              </button>
              <button
                onClick={openWhatsapp}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm bg-emerald-600 text-white text-sm hover:bg-emerald-700 transition-colors"
                data-testid="anam-share-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> Enviar por WhatsApp
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                data-testid="close-anam-result-btn"
              >
                Concluir
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── View answers modal ───────────────────────────
export function AnamnesisViewAnswersModal({ form, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/anamnesis/${form.id}`);
        setDetail(data);
        // Mark as seen (best-effort)
        try { await api.post(`/admin/anamnesis/${form.id}/mark-seen`); } catch { /* noop */ }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erro ao carregar ficha.");
      } finally {
        setLoading(false);
      }
    })();
  }, [form.id]);

  const answers = detail?.answers || {};
  const schema = detail?.schema;
  const hypotheses = detail?.hypotheses;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="view-anamnesis-modal"
    >
      <div
        className="bg-card border border-border rounded-sm p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-accent mb-1">Ficha de Anamnese</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">
              {form.patient_name || "Paciente"}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {form.submitted_at && `Enviado em ${new Date(form.submitted_at).toLocaleString("pt-BR")}`}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div>
        ) : (
          <div className="space-y-6" data-testid="anam-view-content">
            {/* Hypotheses summary */}
            {hypotheses && (
              <section className="rounded-sm border border-emerald-500/50 bg-emerald-50/50 p-4">
                <div className="overline text-emerald-800 mb-1">Hipóteses de carência nutricional</div>
                <div className="text-xs text-muted-foreground mb-3">
                  {hypotheses.total_symptoms_positive} sintoma(s) marcado(s) como "Sim". Cruzamento
                  automático com a tabela sintoma → vitamina/mineral.
                </div>
                {hypotheses.by_nutrient.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">Nenhuma hipótese acionada.</p>
                ) : (
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {hypotheses.by_nutrient.map((n) => (
                      <li
                        key={n.code}
                        className="rounded-sm bg-card border border-emerald-500/30 p-3"
                        data-testid={`hypo-${n.code}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-sm">{n.label}</div>
                          <span className="text-xs font-bold text-emerald-800 bg-emerald-100 rounded-sm px-1.5 py-0.5">
                            {n.count} sintoma{n.count > 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {n.symptoms.map((s) => s.label).join(" · ")}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {hypotheses.bristol && (
                  <div className="mt-3 pt-3 border-t border-emerald-500/30 text-sm">
                    <span className="font-semibold">Escala de Bristol:</span>{" "}
                    Tipo {hypotheses.bristol.type} — {hypotheses.bristol.description}
                    <div className="text-xs text-emerald-900 mt-0.5">
                      {hypotheses.bristol.band_label}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Basic */}
            {schema && schema.basic_questions.some((q) => answers[q.id]) && (
              <section>
                <div className="overline text-muted-foreground mb-1">Dados básicos</div>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {schema.basic_questions.map((q) => (
                    <div key={q.id} className="border border-border rounded-sm p-2">
                      <dt className="text-xs text-muted-foreground">{q.label}</dt>
                      <dd className="font-medium">{answers[q.id] || "—"}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {/* Symptoms */}
            {schema && (
              <section>
                <div className="overline text-muted-foreground mb-1">Respostas Sim/Não</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                  {schema.symptom_questions.map((q) => {
                    const v = answers[q.id];
                    const yes = v === true || v === "sim";
                    return (
                      <div
                        key={q.id}
                        className={`flex items-center justify-between border border-border rounded-sm px-2 py-1 ${
                          yes ? "bg-emerald-50" : ""
                        }`}
                      >
                        <span className={yes ? "font-medium" : "text-muted-foreground"}>{q.label}</span>
                        <span
                          className={`text-xs font-semibold ${
                            yes ? "text-emerald-800" : "text-muted-foreground"
                          }`}
                        >
                          {yes ? "Sim" : "Não"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── Main page ───────────────────────────
export default function AdminAnamnesisPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/anamnesis");
      setForms(data || []);
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const copyLink = async (token) => {
    const link = publicAnamnesisLink(token);
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado!");
    } catch {
      toast.error("Copie manualmente: " + link);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Excluir a ficha de "${f.patient_name || "sem nome"}"?`)) return;
    try {
      await api.delete(`/admin/anamnesis/${f.id}`);
      toast.success("Ficha excluída.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir.");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-anamnesis-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3 inline-flex items-center gap-2">
            <ClipboardCheck className="w-3.5 h-3.5" /> Biblioteca
          </div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">
            Ficha de Anamnese
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Formulário público de pré-consulta com sinais e sintomas + Escala de Bristol. O sistema
            cruza automaticamente cada sintoma com a tabela vitaminas/minerais para gerar hipóteses
            de carência nutricional.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-anamnesis-btn"
        >
          <Plus className="w-4 h-4" /> Nova ficha
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground overline">Carregando…</div>
      ) : forms.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Nenhuma ficha criada ainda. Clique em <span className="font-semibold">&quot;Nova ficha&quot;</span> para gerar seu primeiro link.
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="divide-y divide-border">
            {forms.map((f) => {
              const meta = STATUS_META[f.status] || STATUS_META.pending;
              const Icon = meta.icon;
              return (
                <div key={f.id} className="p-4 hover:bg-muted/40 transition-colors" data-testid={`anam-row-${f.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <div className="font-heading font-semibold">{f.patient_name || "Sem nome"}</div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </div>
                      {f.patient_email && (
                        <div className="text-xs text-muted-foreground mt-1">{f.patient_email}</div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        Criada {new Date(f.created_at).toLocaleString("pt-BR")}
                        {f.expires_at && <> · Expira {new Date(f.expires_at).toLocaleDateString("pt-BR")}</>}
                        {f.submitted_at && (
                          <> · <span className="text-emerald-700">Respondida {new Date(f.submitted_at).toLocaleString("pt-BR")}</span></>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {f.status === "pending" && (
                        <button
                          onClick={() => copyLink(f.token)}
                          className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors inline-flex items-center gap-1 text-xs px-2"
                          data-testid={`anam-copy-${f.id}`}
                        >
                          <Link2 className="w-3.5 h-3.5" /> Copiar link
                        </button>
                      )}
                      {f.status === "submitted" && (
                        <button
                          onClick={() => setViewing(f)}
                          className="p-1.5 rounded-sm border border-primary text-primary hover:bg-primary/5 transition-colors inline-flex items-center gap-1 text-xs px-2"
                          data-testid={`anam-view-${f.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> Ver respostas
                        </button>
                      )}
                      <button
                        onClick={() => remove(f)}
                        className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
                        data-testid={`anam-delete-${f.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {creating && <AnamnesisCreateModal onClose={() => setCreating(false)} onCreated={() => load()} />}
      {viewing && <AnamnesisViewAnswersModal form={viewing} onClose={() => { setViewing(null); load(); }} />}
    </div>
  );
}
